/**
 * Management leadership cockpit — decision KPIs, load/schedule, Ask AI.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { Button } from '../../components/ui/button';
import { FileDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import LeadershipChrome from '@/components/LeadershipChrome';
import LeadershipPageHeader from '@/components/LeadershipPageHeader';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import ManagementFilterBar from './ManagementFilterBar';
import { buildMgmtQuery, computeRange } from './managementRange';
import OnDutyTodayStrip from '@/modules/on-duty/components/OnDutyTodayStrip';
import ManagementKpiRow from './ManagementKpiRow';
import ManagementAiSearch from './ManagementAiSearch';
import ManagementLoadSection from './ManagementLoadSection';
import ManagementScheduleSection from './ManagementScheduleSection';
import ManagementCareRiskSection from './ManagementCareRiskSection';
import ManagementDrillDrawer from './ManagementDrillDrawer';
import { withLoadIndex } from './managementLoadUtils';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ManagementDashboard() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [period, setPeriod] = useState('month');
  const [specialisation, setSpecialisation] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [filterOpts, setFilterOpts] = useState({ doctors: [], specialisations: [] });
  const range = useMemo(() => computeRange(period), [period]);
  const query = useMemo(
    () => buildMgmtQuery({ from: range.from, to: range.to, doctorId, specialisation }),
    [range.from, range.to, doctorId, specialisation],
  );

  const [overview, setOverview] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/management/filter-options`, { withCredentials: true })
      .then((r) => setFilterOpts({
        doctors: r.data?.doctors || [],
        specialisations: r.data?.specialisations || [],
      }))
      .catch(() => setFilterOpts({ doctors: [], specialisations: [] }));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [o, w, r, s, rs] = await Promise.all([
        axios.get(`${API}/api/management/overview?${query}`, { withCredentials: true }),
        axios.get(`${API}/api/management/doctors/workload?${query}`, { withCredentials: true }),
        axios.get(`${API}/api/management/referrals?${query}`, { withCredentials: true }),
        axios.get(`${API}/api/management/schedule-drugs?${query}`, { withCredentials: true }),
        axios.get(`${API}/api/management/roster?${query}`, { withCredentials: true }),
      ]);
      setOverview(o.data);
      setWorkload(w.data);
      setReferrals(r.data);
      setSchedule(s.data);
      setRoster(rs.data);
    } catch (_) {
      toast.error('Failed to load management data');
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [query]);

  const docs = useMemo(
    () => withLoadIndex(workload?.doctors || []),
    [workload],
  );

  const downloadPdf = (kind, doctor_id = null) => {
    const params = new URLSearchParams({ kind, from: range.from, to: range.to });
    if (doctor_id || doctorId) params.set('doctor_id', doctor_id || doctorId);
    if (specialisation) params.set('specialisation', specialisation);
    window.open(`${API}/api/management/report.pdf?${params}`, '_blank', 'noopener');
  };

  const chromeActions = (
    <>
      <Button variant="ghost" size="sm" onClick={loadAll} className="text-white hover:bg-white/10" data-testid="mgmt-refresh">
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
      </Button>
      <Button size="sm" onClick={() => downloadPdf('hospital')}
        className="bg-[#F15A22] text-white hover:brightness-110 font-bold" data-testid="mgmt-pdf-hospital">
        <FileDown className="h-3.5 w-3.5 mr-1.5" /> Hospital PDF
      </Button>
    </>
  );

  return (
    <LeadershipChrome actions={chromeActions} homeHref="/management" roleLabel="Leadership" showLeadershipMenu>
      <div className={`${LEADERSHIP_CONTENT} space-y-6`}>
        <LeadershipPageHeader
          title="Operations cockpit"
          subtitle={`${tenant?.name || 'IIT Hyderabad Hospital'} · Decision view · IST${user?.name ? ` · ${user.name}` : ''}`}
        />
        <ManagementFilterBar
          period={period} setPeriod={setPeriod}
          specialisation={specialisation} setSpecialisation={setSpecialisation}
          doctorId={doctorId} setDoctorId={setDoctorId}
          doctors={filterOpts.doctors} specialisations={filterOpts.specialisations}
          rangeLabel={`${range.label} (${range.from} → ${range.to})`}
          onClear={() => { setPeriod('month'); setSpecialisation(''); setDoctorId(''); }}
        />

        <ManagementKpiRow
          loading={loading}
          kpis={overview?.kpis}
          scheduleTotal={schedule?.total}
          rangeLabel={range.label}
          onOpen={(kind) => setDrawer({ kind })}
        />

        <ManagementAiSearch queryString={query} />
        <OnDutyTodayStrip />

        <ManagementLoadSection
          overview={overview}
          workload={workload}
          docs={docs}
          rangeLabel={range.label}
          onOpenDoctors={() => setDrawer({ kind: 'doctors' })}
          downloadPdf={downloadPdf}
        />

        <ManagementScheduleSection
          roster={roster}
          rangeLabel={range.label}
          onOpen={() => setDrawer({ kind: 'roster' })}
        />

        <ManagementCareRiskSection
          referrals={referrals}
          schedule={schedule}
          onOpen={(kind) => setDrawer({ kind })}
        />
      </div>

      <ManagementDrillDrawer
        drawer={drawer}
        onClose={() => setDrawer(null)}
        workload={workload}
        referrals={referrals}
        schedule={schedule}
        roster={roster}
        downloadPdf={downloadPdf}
      />
    </LeadershipChrome>
  );
}
